import { makeAuthenticatedRequest } from './apiService';
import { API_ENDPOINTS } from '../config/api';
import type { AstsPublicLienFields } from '../components/AstsPublicLienholders';

// ---- Types ----

export interface BulkAstsPublicItem {
  vin: string;
  stateCode: string;
  referenceNote?: string;
}

export interface BulkAstsPublicResultItem {
  index: number;
  vin: string;
  stateCode: string;
  referenceNote?: string;
  success: boolean;
  errorMessage?: string;
  /** While the state is rate-limiting our searches: when they resume (UTC, ISO). */
  availableAgainAtUtc?: string;
  titleResponse?: {
    success: boolean;
    titles?: Array<AstsPublicLienFields & {
      vin?: string;
      titleNumber?: string;
      titleStatus?: string;
      titleDate?: string;
      titleType?: string;
      titleFormat?: string;
      controlNumber?: string;
      purchasePrice?: number;
      year?: number;
      make?: string;
      model?: string;
      bodyStyle?: string;
      color?: string;
      ownerName?: string;
      ownerAddress?: string;
      ownerCity?: string;
      ownerState?: string;
      ownerZip?: string;
      ownerCounty?: string;
      odometerReading?: number;
      odometerStatus?: string;
      brand?: string;
    }>;
  };
  registrationResponse?: {
    success: boolean;
    registrations?: Array<{
      vin?: string;
      plate?: string;
      plateType?: string;
      registrationStatus?: string;
      registrationDate?: string;
      expirationDate?: string;
      year?: number;
      make?: string;
      model?: string;
      color?: string;
      ownerName?: string;
      ownerAddress?: string;
      ownerCity?: string;
      ownerState?: string;
      ownerZip?: string;
      county?: string;
    }>;
  };
}

export interface BulkAstsPublicResponse {
  batchId: string;
  processedAt: string;
  totalRequested: number;
  totalSucceeded: number;
  totalFailed: number;
  results: BulkAstsPublicResultItem[];
}

// ---- Rate-limit message ----

/**
 * The server says when a rate-limited state's free lookups resume in UTC ("until 1:52 PM UTC").
 * Show that time in the viewer's own time zone instead; any other message is returned unchanged.
 */
export const localizeRateLimitMessage = (message: string, availableAgainAtUtc?: string): string => {
  if (!availableAgainAtUtc) return message;
  const until = new Date(availableAgainAtUtc);
  if (Number.isNaN(until.getTime())) return message;
  const local = until.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  return message.replace(/until \d{1,2}:\d{2} [AP]M UTC/, `until ${local}`);
};

// ---- API Functions ----

export const submitBulkAstsPublic = async (
  items: BulkAstsPublicItem[]
): Promise<BulkAstsPublicResponse> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.ASTS_PUBLIC.BULK, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.title || 'Failed to process bulk request');
  }

  return response.json();
};

/**
 * Generate and download a single ASTS Public Inquiry PDF from a previously-fetched title result.
 * Posts the in-memory titleResponse back to the server so it can render without re-running the
 * (slow, CAPTCHA-gated) public-portal scrape.
 */
export const generateAstsPublicPdf = async (
  stateCode: string,
  vin: string,
  titleResponse: BulkAstsPublicResultItem['titleResponse'],
  requestedBy?: string
): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.ASTS_PUBLIC.PDF(stateCode), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vin, stateCode, titleResponse, requestedBy }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || errorData.detail || errorData.title || 'Failed to generate PDF');
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `ASTS_Public_${stateCode}_${vin}_${new Date().toISOString().slice(0, 10)}.pdf`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

/**
 * Generate and download a single combined PDF from previously-fetched bulk ASTS Public results.
 * Posts the in-memory results back to the server so it can render without re-searching the
 * (slow, uncached) public portals.
 */
export const generateBulkAstsPublicPdf = async (
  results: BulkAstsPublicResultItem[],
  requestedBy?: string
): Promise<void> => {
  const response = await makeAuthenticatedRequest(API_ENDPOINTS.ASTS_PUBLIC.BULK_PDF, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ results, requestedBy }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.title || 'Failed to generate bulk PDF');
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `ASTS_Public_Bulk-${new Date().toISOString().slice(0, 10)}.pdf`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1].replace(/['"]/g, '');
    }
  }

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

// ---- CSV Helpers ----

export const parseAstsPublicCsvItems = (content: string): BulkAstsPublicItem[] => {
  const lines = content.split(/\r?\n/).filter(line => line.trim());
  if (lines.length < 2) {
    throw new Error('CSV file must have a header row and at least one data row');
  }

  const header = lines[0].toLowerCase().replace(/['"]/g, '');
  const hasHeader = header.includes('vin') || header.includes('state');

  const startLine = hasHeader ? 1 : 0;
  const items: BulkAstsPublicItem[] = [];

  for (let i = startLine; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const parts = line.split(',').map(p => p.replace(/['"]/g, '').trim());
    const vin = (parts[0] || '').toUpperCase();
    const stateCode = (parts[1] || '').toUpperCase();
    const referenceNote = parts[2] || undefined;

    if (vin && stateCode) {
      items.push({ vin, stateCode, referenceNote });
    }
  }

  return items;
};

export const downloadAstsPublicCsvTemplate = (): void => {
  const csv = 'VIN,State,ReferenceNote\n5J6RW6H37KL004064,OH,Sample Reference\n';
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'asts_public_bulk_template.csv';
  a.click();
  URL.revokeObjectURL(url);
};
